/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../globalScope/GlobalScope';
import IDisposable from '../lang/IDisposable';
import DisposableList from '../lang/DisposableList';
import FeatureEnablement from '../environment/FeatureEnablement';
import LoggerFactory from '../logger/LoggerFactory';
import {ILogger} from '../logger/LoggerInterface';
import Subject from '../rx/Subject';
import ReadOnlySubject from '../rx/ReadOnlySubject';
import IPeerConnection from './IPeerConnection';
import SdpParser from './SdpParser';

export interface IRtcPublishMonitorStatistic {
  [kind: string]: IRtcPublishStatistic;
}

export interface ILegacyRTCStatsReport {
  result: () => ILegacyRTCStatsReportItem[];
}

export interface ILegacyRTCStatsReportItem extends RTCStatsReport {
  type: string;
  names: () => string[];
  stat: (name: string) => number;
}

export interface IRtcPublishStatistic {
  lastPacketSentTimestamp?: number;
  ssrc: string;
  mediaType: string;
  timestamp: number;
  bitrate?: number;
  bytesSent: number;
  packetsSent: number;
  retransmittedBytesSent?: number;
  firCount?: number;
  frameHeight?: number;
  frameWidth?: number;
  framesEncoded?: number;
  framesSent?: number;
  headerBytesSent?: number;
  hugeFramesSent?: number;
  pliCount?: number;
  qpSum?: number;
  totalEncodeTime?: number;
  totalEncodedBytesTarget?: number;
  codec: string;
  fps?: number;
  roundTripTime: number;
}

const updateStatisticTimeout = 1000;
const getStatsTimeoutDuration = 5000;

export default class RtcPublishMonitor implements IDisposable {
  private readonly _logger: ILogger = LoggerFactory.getLogger('RtcPublishMonitor');
  private readonly _estimatedRoundTripTime: number;
  private readonly _estimatedVideoCodec: string = 'unknown';
  private readonly _estimatedAudioCodec: string = 'unknown';
  private readonly _rtcStatistic: Subject<IRtcPublishMonitorStatistic> = new Subject<IRtcPublishMonitorStatistic>({});
  private readonly _disposables: DisposableList = new DisposableList();
  private _peerConnection: IPeerConnection | null;
  private _isMonitorRunning = true;
  private _updateTimeout: number | undefined = undefined;
  private _monitorGeneration = 0;
  private _availableTracks: {
    audio: boolean;
    video: boolean;
  } | null = null;

  constructor(peerConnection: IPeerConnection, estimatedRoundTripTime: number, isForeground: ReadOnlySubject<boolean>) {
    this._peerConnection = peerConnection;
    this._estimatedRoundTripTime = estimatedRoundTripTime;

    if (this._peerConnection && this._peerConnection?.currentLocalDescription?.sdp && this._peerConnection?.currentRemoteDescription?.sdp) {
      const parsedLocalDescription = new SdpParser(this._peerConnection.currentLocalDescription.sdp);
      const parsedRemoteDescription = new SdpParser(this._peerConnection.currentRemoteDescription.sdp);

      this._estimatedAudioCodec = parsedRemoteDescription.audioCodec;
      this._estimatedVideoCodec = parsedRemoteDescription.videoCodec;

      this._availableTracks = {
        audio: parsedLocalDescription.isAudioTrackEnabled && parsedRemoteDescription.isAudioTrackEnabled,
        video: parsedLocalDescription.isVideoTrackEnabled && parsedRemoteDescription.isVideoTrackEnabled
      };

      let isInitialSubscriptionCallback = true;

      this._disposables.add(
        isForeground.subscribe(foreground => {
          if (isInitialSubscriptionCallback) {
            isInitialSubscriptionCallback = false;

            return;
          }

          if (!foreground || !this._isMonitorRunning) {
            return;
          }

          // Invalidate any in-flight poll from before the freeze
          this._monitorGeneration++;
          GlobalScope.value.clearTimeout(this._updateTimeout);

          this._logger.debug('Restarting stats polling after foreground transition');
          this.updateStatistic();
        })
      );

      this.updateStatistic();
    }
  }

  get rtcStatistic(): Subject<IRtcPublishMonitorStatistic> {
    return this._rtcStatistic;
  }

  public dispose(): void {
    this._monitorGeneration++;
    this._isMonitorRunning = false;
    this._peerConnection = null;
    this._disposables.dispose();
    GlobalScope.value.clearTimeout(this._updateTimeout);
    this._updateTimeout = undefined;
  }

  private async updateStatistic(): Promise<void> {
    if (FeatureEnablement.getStatsPromiseBasedDisabled) {
      const ignored = {
        audio: 0,
        video: 0
      };

      return;
    }

    const generation = this._monitorGeneration;
    // Safari 11 doesnt have kind or mediaType so we need to take it from the inbound-rtp object id
    const getKindForSafari11 = (item: {id: string}): string => item.id.includes('Audio') ? 'audio' : item.id.includes('Video') ? 'video' : '';
    let timeoutHandle: number | undefined;

    try {
      const statsPromise = this._peerConnection?.getStats(null);

      if (!statsPromise) {
        return;
      }

      const timeoutPromise = new Promise<undefined>(resolve => {
        timeoutHandle = GlobalScope.value.setTimeout(() => resolve(undefined), getStatsTimeoutDuration);
      });
      const stats = await Promise.race([statsPromise, timeoutPromise]);

      if (generation !== this._monitorGeneration || !this._isMonitorRunning) {
        return;
      }

      if (!stats) {
        this._logger.debug('getStats() timed out, skipping stats processing');
      } else {
        const rtcStatistics: IRtcPublishMonitorStatistic = {};
        let roundTripTime = this._estimatedRoundTripTime;
        let lastPacketSentTimestamp: number | undefined;

        stats.forEach(report => {
          if (report.type === 'candidate-pair' && report.currentRoundTripTime) {
            roundTripTime = report.currentRoundTripTime * 1000;

            if (rtcStatistics['audio']) {
              rtcStatistics['audio'].roundTripTime = roundTripTime;
            }

            if (rtcStatistics['video']) {
              rtcStatistics['video'].roundTripTime = roundTripTime;
            }
          }

          if (report.type === 'candidate-pair' && report.lastPacketSentTimestamp) {
            lastPacketSentTimestamp = report.lastPacketSentTimestamp;

            if (rtcStatistics['audio']) {
              rtcStatistics['audio'].lastPacketSentTimestamp = lastPacketSentTimestamp;
            }

            if (rtcStatistics['video']) {
              rtcStatistics['video'].lastPacketSentTimestamp = lastPacketSentTimestamp;
            }
          }

          if (report.type === 'outbound-rtp') {
            const kind: 'video' | 'audio' | undefined = report.kind || report.mediaType || getKindForSafari11(report);
            let codec = '';

            stats.forEach(item => {
              if (item.id === report.codecId) {
                codec = item.mimeType;
              }
            });

            if (kind && this._availableTracks?.[kind]) {
              const bytes = report.bytesSent || 0;
              const previousBytesSent = this._rtcStatistic.value?.[kind]?.bytesSent ?? 0;
              const bytesDelta = bytes - previousBytesSent;
              const timeDelta = report.timestamp - this._rtcStatistic.value?.[kind]?.timestamp;
              const bitrate = bytesDelta && timeDelta ? Math.floor(8 * bytesDelta /
                timeDelta * 1000) : 0;

              rtcStatistics[kind] = {
                ssrc: report.ssrc,
                mediaType: kind,
                timestamp: report.timestamp,
                bitrate,
                bytesSent: report.bytesSent,
                packetsSent: report.packetsSent,
                retransmittedBytesSent: report.retransmittedBytesSent,
                codec: codec || this.getCodecByType(kind),
                roundTripTime
              };

              if (report.lastPacketSentTimestamp || lastPacketSentTimestamp) {
                rtcStatistics[kind].lastPacketSentTimestamp = report.lastPacketSentTimestamp || lastPacketSentTimestamp;
              }

              if (kind === 'video') {
                rtcStatistics[kind]['firCount'] = report.firCount;
                rtcStatistics[kind]['frameHeight'] = report.frameHeight;
                rtcStatistics[kind]['frameWidth'] = report.frameWidth;
                rtcStatistics[kind]['framesEncoded'] = report.framesEncoded;
                rtcStatistics[kind]['framesSent'] = report.framesSent;
                rtcStatistics[kind]['headerBytesSent'] = report.headerBytesSent;
                rtcStatistics[kind]['hugeFramesSent'] = report.hugeFramesSent;
                rtcStatistics[kind]['pliCount'] = report.pliCount;
                rtcStatistics[kind]['qpSum'] = report.qpSum;
                rtcStatistics[kind]['totalEncodeTime'] = report.totalEncodeTime;
                rtcStatistics[kind]['totalEncodedBytesTarget'] = report.totalEncodedBytesTarget;

                if (this._rtcStatistic.value?.video?.framesEncoded) {
                  const fps = (report.framesEncoded - this._rtcStatistic.value.video.framesEncoded) /
                      (rtcStatistics[kind].timestamp - this._rtcStatistic.value.video.timestamp) *
                      1000;

                  rtcStatistics[kind]['fps'] = fps ? Math.round(fps * 100) / 100 : 0;
                }
              }
            }
          }
        });

        this._rtcStatistic.value = rtcStatistics;
      }
    } catch (error) {
      if (generation !== this._monitorGeneration || !this._isMonitorRunning) {
        return;
      }

      this._logger.warn('getStats() rejected', error);
    } finally {
      GlobalScope.value.clearTimeout(timeoutHandle);
    }

    if (this._isMonitorRunning && generation === this._monitorGeneration) {
      this._updateTimeout = GlobalScope.value.setTimeout(
        () => this.updateStatistic(), updateStatisticTimeout);
    }
  }

  private getCodecByType(type: string): string {
    switch (type) {
      case 'audio':
        return this._estimatedAudioCodec || 'unknown';
      case 'video':
        return this._estimatedVideoCodec || 'unknown';
      default:
        return 'unknown';
    }
  }
}